import * as Network from "expo-network";
import * as Updates from "expo-updates";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Platform } from "react-native";

import { useTheme } from "./ThemeProvider";

import { Button } from "@/components/atoms/Button";
import { Heading } from "@/components/atoms/Heading";
import { Text } from "@/components/atoms/Text";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/atoms/Modal";
import { ifWeb } from "@/utils/platform";
import { TEST_ID_NETWORK_STATUS } from "@/e2e/testId";

/**
 *
 */
interface Props {
  children: ReactNode;
}

/**
 *
 */
type NetworkStatus = "online" | "slow" | "offline";

/**
 *
 */
interface NetworkStatusContextType {
  status: NetworkStatus;
  isOnline: boolean;
  isSlow: boolean;
}

const GOOGLE_PUBLIC_DNS_IP = "https://8.8.8.8";

/** EAS release channel used by the `dev-e2e` build profile (see eas.json). */
const E2E_UPDATES_CHANNEL = "e2e";

const NetworkStatusContext = React.createContext<NetworkStatusContextType>({
  status: "online",
  isOnline: true,
  isSlow: false,
});

export const useNetworkStatus = () => React.useContext(NetworkStatusContext);

/**
 * Whether the blocking connectivity modal should be shown for a given status.
 *
 * SE-6833: the modal is re-evaluated on a 30-second interval, so on a host
 * whose connectivity probe reads "slow" it can appear over ANY screen at ANY
 * moment. On the CI emulator that is routine, and because the modal renders a
 * full-screen backdrop it swallows the next tap and fails an assertion in a
 * flow that has nothing to do with networking — run 31512248390 lost
 * `Squad Planning - Map View` on `TAB_BAR.SQUAD` with this modal on screen.
 * That is a much better fit for the long-standing "random flow, random
 * platform, never reproducible locally" pattern than any per-flow theory.
 *
 * Suppression must be scoped to the E2E BUILD and to nothing else — this modal
 * is real user-facing UX that explains why the app is misbehaving when someone
 * is offline, so it must never be suppressible in a production build. Two
 * signals are honored:
 *
 * 1. `Updates.channel === "e2e"` — the EAS release channel, which only the
 *    `dev-e2e` profile sets. It means precisely "this binary is the E2E
 *    build", it cannot be confused with any operational toggle, and it cannot
 *    be true in a production build (whose channel is `production`).
 * 2. `EXPO_PUBLIC_NETWORK_MODAL_DISABLED` — an explicit escape hatch for local
 *    debugging.
 *
 * An earlier revision also honored `EXPO_PUBLIC_POSTHOG_DISABLED`, on the
 * grounds that only `dev-e2e` sets it today. That was wrong and is deliberately
 * NOT restored: it couples a user-facing connectivity warning to an ANALYTICS
 * toggle, so anyone disabling PostHog in production — for cost, privacy, or
 * during an incident — would silently also delete the offline warning for real
 * users, with nothing in either name hinting at the connection.
 *
 * Only the modal is suppressed. The context still reports the true status, so
 * nothing consuming `useNetworkStatus` changes behavior.
 * @param status - The measured network status.
 * @returns true when the modal should be rendered.
 */
export const shouldShowConnectivityModal = (status: NetworkStatus): boolean => {
  const suppressed =
    Updates.channel === E2E_UPDATES_CHANNEL ||
    process.env.EXPO_PUBLIC_NETWORK_MODAL_DISABLED === "true";

  return !suppressed && (status === "offline" || status === "slow");
};

/**
 * Provider component that monitors network connectivity status and displays modals for offline/slow connections.
 * @param root0 - Component props
 * @param root0.children - React children nodes to render within the provider
 */
const NetworkStatusProvider = ({ children }: Props) => {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>("online");
  const [hasDismissedModal, setHasDismissedModal] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Derived value - calculated during render instead of in useEffect
  const showModal = shouldShowConnectivityModal(networkStatus);

  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const textColorClass = isDark ? "text-white" : "text-black";

  const checkConnectionSpeed = useCallback(async (): Promise<NetworkStatus> => {
    try {
      // First check basic connectivity with expo-network
      const networkState = await Network.getNetworkStateAsync();
      if (!networkState?.isConnected || !networkState?.isInternetReachable) {
        return "offline";
      }

      // Then test actual connection speed with a small request
      const startTime = Date.now();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      try {
        await fetch(GOOGLE_PUBLIC_DNS_IP, {
          method: "HEAD",
          mode: "no-cors",
          cache: "no-cache",
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        const responseTime = Date.now() - startTime;

        // Classify connection based on response time
        if (responseTime > 3000) {
          return "slow";
        }
        return "online";
      } catch (_error) {
        clearTimeout(timeoutId);
        // If fetch fails or times out, check if it's due to being offline or just slow
        const recheck = await Network.getNetworkStateAsync();
        if (!recheck?.isConnected || !recheck?.isInternetReachable) {
          return "offline";
        }
        return "slow";
      }
    } catch (_error) {
      return "offline";
    }
  }, []);

  const contextValue = React.useMemo(
    () => ({
      status: networkStatus,
      isOnline: networkStatus !== "offline",
      isSlow: networkStatus === "slow",
    }),
    [networkStatus]
  );

  const handleDismissModal = useCallback(() => {
    setHasDismissedModal(true);
  }, []);

  /**
   * The probe runs for the whole session, and dismissing the modal does not
   * stop it (SE-6983).
   *
   * It used to: `hasDismissedModal` cleared the interval, so the LAST status
   * ever measured was the one the scout dismissed. The context went on
   * reporting "offline" forever, which is invisible while the only consumer is
   * a modal that has been dismissed — and load-bearing the moment anything
   * ACTS on connectivity returning. The offline drain does exactly that, so a
   * scout who tapped OK would never sync again this session.
   *
   * Dismissal still suppresses the modal for the session; that part is
   * deliberate and unchanged (re-raising it would reintroduce the SE-6833
   * flake, where a full-screen backdrop swallowed the next tap in an unrelated
   * E2E flow). What changed is that suppressing a piece of UI no longer blinds
   * the app to the state behind it.
   */
  useEffect(() => {
    // Initial check
    checkConnectionSpeed().then(setNetworkStatus);

    // Set up periodic checks every 30 seconds
    intervalRef.current = setInterval(() => {
      checkConnectionSpeed().then(setNetworkStatus);
    }, 30000);

    // Clean up
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkConnectionSpeed]);

  return (
    <NetworkStatusContext.Provider value={contextValue}>
      <Modal
        useRNModal={Platform.OS !== "web"}
        isOpen={showModal && !hasDismissedModal}
      >
        <ModalBackdrop />
        <ModalContent
          className={`max-w-96 ${isDark ? "bg-background-gray" : ifWeb("", "bg-surface-muted")}`}
        >
          <ModalHeader className="items-center justify-center border-b-2 border-b-outline-card">
            <Heading variant="title" className={textColorClass}>
              {networkStatus === "offline"
                ? "No Internet Connection"
                : "Slow Internet Connection"}
            </Heading>
          </ModalHeader>
          <ModalBody>
            <Text
              variant="unstyled"
              className={`text-center ${textColorClass}`}
            >
              {networkStatus === "offline"
                ? "The internet cannot be reached right now. You will be able to continue when you get into an area with better connectivity."
                : "Your internet connection is very slow. Some features may take longer to load or may not work properly."}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="solid"
              tone="primary"
              className={`flex-1 focus:outline-none ${isDark ? "bg-surface-action" : "bg-surface-muted"}`}
              onPress={handleDismissModal}
              label="OK"
              labelTone="primary"
              testID={TEST_ID_NETWORK_STATUS.DISMISS}
            />
          </ModalFooter>
        </ModalContent>
      </Modal>
      {children}
    </NetworkStatusContext.Provider>
  );
};

export { NetworkStatusContext, NetworkStatusProvider };
